import { motion } from "framer-motion";
import { CheckCircle2, MapPin } from "lucide-react";
import { BRAND } from "@/lib/api";
import { IconSticker } from "./Sticker";
import CartoonSticker from "./CartoonStickers";
import Wordmark from "./Wordmark";

/**
 * FloatingScene — premium hero composition with a typed ALLTASK MOVES
 * wordmark blended onto the cartoon poster as a clean signboard plate.
 */
export default function FloatingScene() {
    return (
        <div className="relative aspect-[4/4] w-full md:aspect-[5/5]">
            <div className="absolute inset-0 hero-blob -z-10" />

            {/* Main illustration panel */}
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 18 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="relative h-full w-full overflow-hidden rounded-[40px] border border-[#ECE4D3] bg-gradient-to-br from-[#FFF9EE] via-[#FDF6EE] to-[#FAF0DD] shadow-[0_40px_80px_-30px_rgba(14,42,71,0.25)]"
            >
                <div className="absolute inset-0 bg-grain opacity-40" />
                <picture>
                    <source srcSet={BRAND.illustrationWebp} type="image/webp" />
                    <motion.img
                        src={BRAND.illustration}
                        alt="AllTask Moves illustration"
                        initial={{ scale: 1.04 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                        className="absolute inset-0 h-full w-full object-cover"
                        fetchpriority="high"
                    />
                </picture>
                <div className="absolute inset-0 bg-white/15" />

                {/* Brand signboard — typed wordmark blended onto the poster */}
                <motion.div
                    initial={{ opacity: 0, y: -14, scale: 0.92 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-x-0 top-4 z-10 flex justify-center sm:top-6 md:top-8"
                    data-testid="poster-wordmark"
                >
                    <motion.div
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        className="relative px-6 py-3 sm:px-8 sm:py-4"
                    >
                        {/* Glass signboard plate */}
                        <div className="absolute inset-0 rounded-[20px] glass shadow-[0_18px_40px_rgba(14,42,71,0.15)]" />
                        {/* Tiny pin / nail accents to suggest a signboard */}
                        <span className="absolute left-3 top-2 h-1.5 w-1.5 rounded-full bg-[#F26B1F]/70" />
                        <span className="absolute right-3 top-2 h-1.5 w-1.5 rounded-full bg-[#F26B1F]/70" />
                        <div className="relative">
                            <Wordmark
                                primarySize="text-2xl sm:text-3xl md:text-4xl"
                                secondarySize="text-xs sm:text-sm md:text-base"
                                animate
                            />
                        </div>
                    </motion.div>
                </motion.div>
            </motion.div>

            {/* Glass card — Brand promise (bottom-left of panel) */}
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                className="absolute -bottom-6 left-2 max-w-[240px] rounded-2xl glass px-4 py-3 shadow-[0_18px_40px_rgba(14,42,71,0.18)] sm:-left-8 sm:max-w-[280px]"
            >
                <div className="flex items-center gap-3">
                    <span className="rounded-xl bg-[#0E2A47] p-2 text-white">
                        <CheckCircle2 size={16} />
                    </span>
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#F26B1F]">
                            Brand promise
                        </p>
                        <p className="text-[12px] font-semibold leading-snug text-[#0E2A47]">
                            We don't promise magic. We promise structure.
                        </p>
                    </div>
                </div>
            </motion.div>

            <CartoonSticker name="truck" width={110} rotate={-6} delay={0.45} className="hidden md:inline-flex -top-8 -left-6 truck-roll" />
            <CartoonSticker name="boxes" width={92} rotate={8} delay={0.6} float="big" className="hidden md:inline-flex -top-6 -right-3" />
            <CartoonSticker name="keys" width={88} rotate={-12} delay={0.75} float="slow" className="hidden lg:inline-flex top-1/2 -right-10 -translate-y-1/2" />
            <CartoonSticker name="house" width={92} rotate={6} delay={0.9} className="hidden md:inline-flex -bottom-8 -right-4" />
            <IconSticker icon={MapPin} tone="orange" size={40} rotate={10} delay={1.0} className="absolute -left-3 top-8 hidden md:flex float-y" />
            <span className="pulse-ring absolute right-10 top-1/4 h-3 w-3 rounded-full bg-[#F26B1F]" />
        </div>
    );
}
